import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { useLocation, useNavigate } from "react-router-dom";
import {
  apiRequest,
  getToken,
  SuccessToast,
  ErrorToast,
  toastConfig,
  devError,
} from "@ridecloud/shared";
import { ArrowLeft, Car } from "lucide-react";
import { Toaster } from "react-hot-toast";

// Import components
import SearchBar from "../components/view_ride_page/SearchBar";
import FilterPanel from "../components/view_ride_page/FilterPanel";
import LoadingSkeleton from "../components/view_ride_page/LoadingSkeleton";
import RideCard from "../components/view_ride_page/RideCard";
import Footer from "../components/footer";

function App() {
  const navigate = useNavigate();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // All state from useRideSearch hook
  const [searchQuery, setSearchQuery] = useState("");
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  // Inputs (typing)
  const [fromInput, setFromInput] = useState(queryParams.get("from") || "");
  const [toInput, setToInput] = useState(queryParams.get("to") || "");
  const [selectedDate, setSelectedDate] = useState(
    queryParams.get("date") || ""
  );

  // Committed search values (only updated when Search button is clicked)
  const [fromLocation, setFromLocation] = useState(
    queryParams.get("from") || ""
  );
  const [toLocation, setToLocation] = useState(queryParams.get("to") || "");
  const [selectedSearchDate, setSelectedSearchDate] = useState(
    queryParams.get("date") || ""
  );
  const [isLoading, setIsLoading] = useState(false);
  const [rides, setRides] = useState([]);
  const [fromSuggestions, setFromSuggestions] = useState([]);
  const [toSuggestions, setToSuggestions] = useState([]);
  const [showFromSuggestions, setShowFromSuggestions] = useState(false);
  const [showToSuggestions, setShowToSuggestions] = useState(false);
  const [allCities, setAllCities] = useState([]);

  const [filters, setFilters] = useState({
    priceRange: [0, 2000],
    timeRange: ["00:00", "23:59"],
    minSeats: 1,
    sortBy: "price",
  });

  // Fetch all cities on component mount
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await apiRequest(async (APIv1) =>
          APIv1.get("/user/dashboard/get_cities", {
            headers: {
              Authorization: `Bearer ${await getToken()}`,
              "Content-Type": "application/json",
            },
          })
        );
        setAllCities(response.data);
      } catch (error) {
        devError("Error fetching cities:", error);
        // fallback minimal list
      }
    };

    fetchCities();
  }, []);

  // Handle search button click
  const handleSearch = useCallback(async () => {
    if (!fromInput && !toInput && selectedDate) {
      setSelectedSearchDate(selectedDate);

      setHasSearched(true);
      setIsLoading(true);

      try {
        const token = await getToken();
        const resp = await apiRequest(async (APIv1) =>
          APIv1.get("/user/dashboard/get_all_rides", {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            params: {
              date: selectedDate,
            },
          })
        );

        setRides(resp.data || []);
      } catch (err) {
        ErrorToast("Error fetching rides. Please try again later.");
        devError("Error fetching all rides:", err);
      } finally {
        setIsLoading(false);
      }
    } else if (!fromInput || !toInput || !selectedDate) {
      ErrorToast("Please fill in all required fields: From, To, and Date");
      return;
    } else {
      // Commit input -> search state
      setFromLocation(fromInput);
      setToLocation(toInput);
      setSelectedSearchDate(selectedDate);

      setHasSearched(true);
      setIsLoading(true);

      try {
        const token = await getToken();
        const resp = await apiRequest(async (APIv1) =>
          APIv1.get("/user/dashboard/get_rides", {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            params: {
              pickup: fromInput,
              destination: toInput,
              date: selectedDate,
              minPrice: filters.priceRange[0],
              maxPrice: filters.priceRange[1],
              minSeats: filters.minSeats,
              sortBy: filters.sortBy,
              search: searchQuery,
            },
          })
        );

        setRides(resp.data || []);
      } catch (err) {
        devError("Error fetching rides:", err);
        ErrorToast("Error fetching rides. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    }
  }, [fromInput, toInput, selectedDate, filters, searchQuery]);

  // Automatically search when page loads with query params (if provided)
  useEffect(() => {
    if (isInitialLoad && selectedDate) {
      handleSearch();
      setIsInitialLoad(false);
    }
  }, [isInitialLoad, fromInput, toInput, selectedDate, handleSearch]);

  // Filter cities based on input for from location
  useEffect(() => {
    const delay = setTimeout(() => {
      if (fromInput.length > 1) {
        const filtered = allCities.filter((city) =>
          city.toLowerCase().includes(fromInput.toLowerCase())
        );
        setFromSuggestions(filtered);
      } else {
        setFromSuggestions([]);
      }
    }, 300);

    return () => clearTimeout(delay);
  }, [fromInput, allCities]);

  // Filter cities based on input for to location
  useEffect(() => {
    const delay = setTimeout(() => {
      if (toInput.length > 1) {
        const filtered = allCities.filter((city) =>
          city.toLowerCase().includes(toInput.toLowerCase())
        );
        setToSuggestions(filtered);
      } else {
        setToSuggestions([]);
      }
    }, 300);

    return () => clearTimeout(delay);
  }, [toInput, allCities]);

  // Handle from suggestion selection
  const handleFromSuggestionSelect = (city) => {
    setFromInput(city);
    setFromSuggestions([]);
    setShowFromSuggestions(false);
  };

  // Handle to suggestion selection
  const handleToSuggestionSelect = (city) => {
    setToInput(city);
    setToSuggestions([]);
    setShowToSuggestions(false);
  };

  // Helper functions and client-side filters
  const getVehicleType = (vehicleName) => {
    const name = vehicleName.toLowerCase();
    if (name.includes("tesla") || name.includes("bmw") || name.includes("audi"))
      return "luxury";
    if (name.includes("suv") || name.includes("cr-v") || name.includes("x5"))
      return "suv";
    if (name.includes("transit") || name.includes("van")) return "minivan";
    return "car";
  };

  const getTotalAvailableSeats = (ride) =>
    ride.vehicle.seats.front.available_seats +
    ride.vehicle.seats.middle.available_seats +
    ride.vehicle.seats.back.available_seats;

  const getLowestPrice = (ride) => {
    const prices = [];
    if (ride.vehicle.seats.front.available_seats > 0)
      prices.push(ride.vehicle.seats.front.price);
    if (ride.vehicle.seats.middle.available_seats > 0)
      prices.push(ride.vehicle.seats.middle.price);
    if (ride.vehicle.seats.back.available_seats > 0)
      prices.push(ride.vehicle.seats.back.price);
    return prices.length > 0 ? Math.min(...prices) : 0;
  };
  const swapHandler = () => {
    setFromInput(toInput);
    setToInput(fromInput);

    setFromSuggestions(toSuggestions);
    setToSuggestions(fromSuggestions);
  };
  const filteredAndSortedRides = useMemo(() => {
    if (!hasSearched) return [];

    let filtered = rides.filter((ride) => {
      if (!ride.isAvailable) return false;

      const rideDate = new Date(ride.date).toISOString().split("T")[0];
      if (rideDate !== selectedSearchDate) return false;

      const lowest = getLowestPrice(ride);
      if (lowest < filters.priceRange[0] || lowest > filters.priceRange[1])
        return false;

      if (getTotalAvailableSeats(ride) < filters.minSeats) return false;

      const matchesFrom =
        fromLocation === "" ||
        ride.pickupPoints.some((point) =>
          point.toLowerCase().includes(fromLocation.toLowerCase())
        );
      const matchesTo =
        toLocation === "" ||
        ride.dropoffPoints.some((point) =>
          point.toLowerCase().includes(toLocation.toLowerCase())
        );

      if (!matchesFrom || !matchesTo) return false;

      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
          (ride.userId.first_name + " " + ride.userId.last_name)
            .toLowerCase()
            .includes(q) ||
          ride.vehicle.name.toLowerCase().includes(q) ||
          ride.pickupPoints.some((point) => point.toLowerCase().includes(q)) ||
          ride.dropoffPoints.some((point) => point.toLowerCase().includes(q))
        );
      }

      return true;
    });

    filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case "price":
          return getLowestPrice(a) - getLowestPrice(b);
        case "time":
          return a.time - b.time;
        case "availability":
          return getTotalAvailableSeats(b) - getTotalAvailableSeats(a);
        default:
          return 0;
      }
    });

    return filtered;
  }, [
    rides,
    filters,
    searchQuery,
    fromLocation,
    toLocation,
    selectedSearchDate,
    hasSearched,
  ]);

  const handleCall = useCallback((phoneNumber) => {
    if (
      typeof window !== "undefined" &&
      /Mobi|Android/i.test(navigator.userAgent)
    ) {
      // Mobile: open dialer
      window.location.href = `tel:${phoneNumber}`;
    } else {
      // Desktop: alert as fallback
      ErrorToast(
        `Calling is not supported on desktop. Use this number: ${phoneNumber}`
      );
    }
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <Toaster {...toastConfig} />
      {/* Header */}
      <header
        className="bg-white shadow-sm border-b border-emerald-100"
        style={{
          paddingTop: Capacitor.isNativePlatform()
            ? "env(safe-area-inset-top)"
            : undefined,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div
                onClick={() => navigate("/search-rides")}
                className="group w-10 h-10 rounded-2xl flex items-center justify-center bg-gray-200 text-gray-700 hover:bg-gray-300 backdrop-blur-sm hover:border-white/30 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer"
              >
                <ArrowLeft
                  color="grey"
                  className="w-6 h-6 text-white group-hover:-translate-x-1 transition-transform duration-300"
                />
              </div>

              <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                <img
                  src="/img/ride_cloud.png"
                  alt="icon"
                  className="w-10 h-10 sm:w-10 sm:h-10"
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">RideCloud</h1>
                <p className="text-sm text-gray-600">Find your perfect ride</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Section */}
        <SearchBar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          fromInput={fromInput}
          toInput={toInput}
          swapHandler={swapHandler}
          onFromChange={setFromInput}
          onToChange={setToInput}
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
          fromSuggestions={fromSuggestions}
          toSuggestions={toSuggestions}
          onFromSuggestionSelect={handleFromSuggestionSelect}
          onToSuggestionSelect={handleToSuggestionSelect}
          showFromSuggestions={showFromSuggestions}
          showToSuggestions={showToSuggestions}
          setShowFromSuggestions={setShowFromSuggestions}
          setShowToSuggestions={setShowToSuggestions}
          onSearch={handleSearch}
        />

        {/* Filters */}
        <FilterPanel
          filters={filters}
          onFiltersChange={setFilters}
          isOpen={isFilterOpen}
          onToggle={() => setIsFilterOpen(!isFilterOpen)}
        />

        {/* Results Header */}
        {hasSearched && !isLoading && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 sm:mb-6">
            <h2 className="text-sm sm:text-xl font-semibold text-gray-900 mb-1 sm:mb-0">
              {fromLocation && toLocation
                ? `${filteredAndSortedRides.length} rides found from ${fromLocation} to ${toLocation}`
                : `Total ${filteredAndSortedRides.length} rides found`}
            </h2>
            <div className="text-xs sm:text-sm text-gray-600">
              Showing results for{" "}
              {selectedSearchDate
                ? new Date(selectedSearchDate).toLocaleDateString("en-GB")
                : "-"}
            </div>
          </div>
        )}

        {/* Rides Grid */}
        {isLoading ? (
          <LoadingSkeleton />
        ) : hasSearched && filteredAndSortedRides.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAndSortedRides.map((ride, index) => (
              <RideCard key={index} ride={ride} onCall={handleCall} />
            ))}
          </div>
        ) : hasSearched ? (
          <div className="text-center py-16">
            <div className="bg-white rounded-2xl shadow-lg p-8 inline-block">
              <div className="bg-gray-100 rounded-full p-4 mb-4 inline-block">
                <Car className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No rides found
              </h3>
              <p className="text-gray-600 max-w-sm">
                Try adjusting your filters or search for a different route and
                date
              </p>
            </div>
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="bg-white rounded-2xl shadow-lg p-8 inline-block">
              <div className="bg-emerald-100 rounded-full p-4 mb-4 inline-block">
                <Car className="h-8 w-8 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Find your perfect ride
              </h3>
              <p className="text-gray-600 max-w-sm">
                Enter your pickup, destination, and date to search for available
                rides
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
